import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { ChevronDown, Download, Loader2, Sparkles, Tags, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { TraceId } from '@/components/ui/trace-id'
import {
  runDocumentAction,
  type ActionProgress,
  type DocumentAction,
} from '@/lib/api/documents/actions'
import { apiErrorMessage, traceIdOf } from '@/lib/api-client'
import { exportDocumentTerms } from '@/lib/api/documents/export-terms'
import { useLocalizedPath } from '@/lib/i18n/routes'

interface DocumentActionsProps {
  selected: number[]
  /** Renders just the button (for a header), not the full selection bar. */
  compact?: boolean
  collectionId: number | null
  onClearSelection: () => void
}

/** Label and hint keys live in the catalogue; only the icon is fixed here. */
const ACTIONS: { id: DocumentAction; icon: typeof Tags }[] = [
  { id: 'extract_terms', icon: Tags },
  { id: 'detect_metadata', icon: Sparkles },
]

const ACTION_KEY = {
  extract_terms: { label: 'actions.extractTerms', hint: 'actions.extractTermsHint' },
  detect_metadata: { label: 'actions.detectMetadata', hint: 'actions.detectMetadataHint' },
} as const

export function DocumentActions({
  selected,
  compact = false,
  collectionId,
  onClearSelection,
}: DocumentActionsProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const path = useLocalizedPath()
  const [open, setOpen] = useState(false)
  const [progress, setProgress] = useState<ActionProgress | null>(null)
  const [failures, setFailures] = useState<{ title: string; detail: string }[]>([])
  const [downloading, setDownloading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const run = async (action: DocumentAction) => {
    setOpen(false)
    setFailures([])
    setProgress({ stage: 'document', percentage: 0 })
    abortRef.current = new AbortController()
    const failed: { title: string; detail: string; jobId?: number }[] = []
    let lastJobId: number | undefined
    try {
      await runDocumentAction(
        selected,
        action,
        (p) => {
          if (p.stage === 'document') {
            if (p.job_id) lastJobId = p.job_id
            if (p.status === 'error') {
              failed.push({ title: p.title ?? '', detail: p.detail ?? '', jobId: p.job_id })
            }
          }
          if (p.stage !== 'keepalive') setProgress(p)
        },
        abortRef.current.signal,
      )
      // Terms, counts and extraction status all moved.
      qc.invalidateQueries({ queryKey: ['documents', collectionId] })
      qc.invalidateQueries({ queryKey: ['terms'] })
      qc.invalidateQueries({ queryKey: ['collections'] })
      qc.invalidateQueries({ queryKey: ['domains'] })
      setFailures(failed)
      if (!failed.length) {
        toast.success(t('actions.done'), {
          description: t('actions.processed', { count: selected.length }),
          // Opens the journal entry: prompt, input and raw model output.
          action: lastJobId
            ? {
                label: t('actions.viewJob'),
                onClick: () =>
                  navigate({ pathname: path('jobs'), search: `?too=${lastJobId}` }),
              }
            : undefined,
        })
        onClearSelection()
      } else {
        // The id goes in the toast itself: the failure list is not rendered in
        // the compact (document header) form, so "see the list below" would
        // point at nothing.
        const first = failed[0]
        const id = first.detail.match(/\(ID ([0-9a-f]{8})\)\s*$/)?.[1]
        const text = id ? first.detail.replace(/\s*\(ID [0-9a-f]{8}\)\s*$/, '') : first.detail
        toast.error(
          failed.length === 1
            ? t('actions.oneFailed')
            : t('actions.someFailed', { count: failed.length }),
          {
            duration: 30_000, // long enough to copy the id
            action: first.jobId
              ? {
                  label: t('actions.viewJob'),
                  onClick: () =>
                    navigate({ pathname: path('jobs'), search: `?too=${first.jobId}` }),
                }
              : undefined,
            description: (
              <span className="flex flex-wrap items-center gap-1">
                <span>
                  {failed.length === 1
                    ? text
                    : t('actions.firstFailure', { title: first.title, detail: text })}
                </span>
                {id && <TraceId id={id} />}
              </span>
            ),
          },
        )
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        const tid = traceIdOf(e)
        toast.error(t('actions.failed'), {
          description: apiErrorMessage(e) + (tid ? ` · ${t('common.traceId', { id: tid })}` : ''),
        })
      }
    } finally {
      setProgress(null)
    }
  }

  if (!selected.length) return null

  const download = async () => {
    setOpen(false)
    setDownloading(true)
    try {
      await exportDocumentTerms(selected)
    } catch (e) {
      toast.error(t('terms.exportFailed'), { description: apiErrorMessage(e) })
    } finally {
      setDownloading(false)
    }
  }

  // In the header form extraction is promoted to its own button, so the menu
  // holds only the rest; the bulk bar keeps everything under one menu.
  const menuActions = compact ? ACTIONS.filter((a) => a.id !== 'extract_terms') : ACTIONS

  const menu = (align: 'left' | 'right') => (
    <div className="relative">
      <Button variant="outline" size="sm" disabled={downloading} onClick={() => setOpen((v) => !v)}>
        {t('common.actions')}
        {downloading ? <Loader2 className="animate-spin" /> : <ChevronDown />}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={cn(
              'absolute top-full z-50 mt-1 w-64 overflow-hidden rounded-md border border-border bg-card shadow-lg',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {menuActions.map(({ id, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => run(id)}
                className="flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left hover:bg-muted"
              >
                <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-[13px]">{t(ACTION_KEY[id].label)}</p>
                  <p className="text-[10px] text-muted-foreground">{t(ACTION_KEY[id].hint)}</p>
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted disabled:opacity-60"
            >
              <Download className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-[13px]">{t('documents.downloadXlsx')}</p>
                <p className="text-[10px] text-muted-foreground">{t('actions.downloadHint')}</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  )

  const running = progress ? (
    <div className="flex items-center gap-2">
      <Loader2 className="size-3.5 animate-spin text-primary" />
      <div className="h-1 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${Math.max(progress.percentage, 0)}%` }}
        />
      </div>
      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
        {progress.index ?? 0}/{progress.total ?? selected.length}
      </span>
      <Button variant="ghost" size="sm" onClick={() => abortRef.current?.abort()}>
        {t('common.abort')}
      </Button>
    </div>
  ) : null

  // Header form: no selection chrome, just the controls for the open document.
  // Extraction is the reason this page exists, so it gets its own button; the
  // rarer actions stay behind the menu.
  if (compact)
    return (
      running ?? (
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            disabled={downloading}
            title={t('actions.extractTermsHint')}
            onClick={() => run('extract_terms')}
          >
            <Tags />
            {t('actions.extractTerms')}
          </Button>
          {menu('right')}
        </div>
      )
    )

  return (
    <div className="border-b border-border bg-accent/40 px-3 py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium">
          {t('actions.selected', { count: selected.length })}
        </span>

        {progress ? (
          <div className="flex flex-1 items-center gap-2">
            <Loader2 className="size-3.5 animate-spin text-primary" />
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.max(progress.percentage, 0)}%` }}
              />
            </div>
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {progress.index ?? 0}/{progress.total ?? selected.length}
            </span>
            <Button variant="ghost" size="sm" onClick={() => abortRef.current?.abort()}>
              {t('common.abort')}
            </Button>
          </div>
        ) : (
          <>
            {menu('left')}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClearSelection}
              title={t('common.clearSelection')}
            >
              <X />
            </Button>
          </>
        )}
      </div>

      {progress?.title && (
        <p className={cn('mt-1 truncate text-[10px] text-muted-foreground')}>
          {progress.title} — {progress.detail}
        </p>
      )}

      {!!failures.length && (
        <div className="mt-1.5 space-y-0.5 rounded border border-danger/30 bg-danger/10 p-1.5">
          {failures.slice(0, 5).map((f, i) => {
            // The backend appends "(ID xxxxxxxx)"; lift it out so it can be copied.
            const m = f.detail.match(/\(ID ([0-9a-f]{8})\)\s*$/)
            return (
              <p key={i} className="truncate text-[10px] text-danger">
                {f.title}: {m ? f.detail.slice(0, m.index).trim() : f.detail}{' '}
                {m && <TraceId id={m[1]} />}
              </p>
            )
          })}
          {failures.length > 5 && (
            <p className="text-[10px] text-danger">
              {t('actions.andMore', { count: failures.length - 5 })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
